import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import API from '../../../../config/API';
import siteSetting from '../../../../config/env';
import { hairdresserService } from '../../../../_services/hairdresser.service';
import { newServices } from '../../../../_services/newservices.service';
import { salonService } from '../../../../_services/salon.service';
import { settingService } from '../../../../_services/setting.service';
import { diarySharingService } from '../../../../_services/diarySharing.service';
import { normaliseImage } from '../../../shared/normaliseImage';
import { firstWeekOf, isMultiWeek, staffEditForm } from './team';

/**
 * The invited stylist's and employed staff member's setup, talking to the
 * real account. Every call here is one the app already makes for this person
 * on their own Settings, Team or Catalogue pages, wrapped so the chapters can
 * read it, save it the moment it changes, and say plainly when it didn't.
 *
 * Nothing here writes a salon-level setting. A person's own record, their own
 * rota, their own rows on services, their own sharing and booking switches,
 * and their own cancellation policy are all theirs.
 *
 * The shapes and quirks are documented in the setup-build API maps
 * (api-team, api-menu, api-money-rules, api-page-and-messages).
 */

/* ---------------- who am I ---------------- */

export const myId = () => localStorage.getItem('userId') || '';

/** Every id this person's own row might be filed under. Screens disagree. */
export function myIds(user) {
  const info = (user && user.userInfo) || {};
  const h = info.hairdresser || {};
  return [myId(), info.userId, h.userId, h._id, h.hairdresserId]
    .filter(Boolean)
    .map(String)
    .filter((v, i, all) => all.indexOf(v) === i);
}

export const firstWord = (name) => String(name || '').trim().split(/\s+/)[0] || '';
export const norm = (s) => String(s || '').trim().toLowerCase();
export const currencyOf = (user) => (user && user.userInfo && user.userInfo.hairdresser && user.userInfo.hairdresser.currencySymbol) || '£';

/* ---------------- errors, said plainly ---------------- */

export class SetupError extends Error {
  constructor(message) {
    super(message);
    this.plain = true;
  }
}

const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

/** A refusal that came back as HTTP 200. */
function refusal(res, fallback) {
  const d = (res && res.data) || {};
  return text(d.error && (d.error.responseMessage || d.error.message)) || text(d.messages) || text(d.message) || fallback;
}

/** Anything thrown, as one sentence someone can act on. */
export function reasonOf(e, fallback = 'That didn’t go through. Try again.') {
  if (e && e.plain) return e.message;
  const d = e && e.response && e.response.data;
  if (d) return text(d.error && (d.error.responseMessage || d.error.message)) || text(d.messages) || text(d.message) || fallback;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'You’re offline. Check your connection, then try again.';
  return fallback;
}

/* ---------------- the team (one call, shared briefly) ---------------- */

let teamCache = null;

/**
 * Everyone at this location with their rota, from the list Team > Schedules
 * renders (api-team 1). Read with the service directly: dispatching the redux
 * action would overwrite the Team page's own copy.
 */
export function loadTeam({ fresh = false } = {}) {
  if (!fresh && teamCache && Date.now() - teamCache.at < 60000) return teamCache.promise;
  const promise = hairdresserService
    .staffRotaV2({ hairdresserId: myId(), searchKey: '', page: 1, listType: 1, isGetRota: 1 })
    .then((res) => {
      const d = res && res.data;
      if (!d || d.statusCode !== 1) throw new SetupError(refusal(res, 'I couldn’t load the team just now.'));
      const rows = (d.responseData && d.responseData.result) || [];
      return rows.filter(Boolean).filter((r) => r.name !== 'Unknown' && r.email !== 'Unknown');
    });
  teamCache = { at: Date.now(), promise };
  promise.catch(() => { if (teamCache && teamCache.promise === promise) teamCache = null; });
  return promise;
}

export const ownerOf = (rows) => (rows || []).find((r) => Number(r && r.hairdresserInfo && r.hairdresserInfo.isSalonOwner) === 1) || null;
export const mineOf = (rows, ids) => (rows || []).find((r) => r && ids.includes(String(r._id))) || null;

/* ---------------- profile: photo, job title, bio ---------------- */

/** One person's full editable record (api-team 3). */
export async function loadEditInfo(id) {
  const res = await hairdresserService.getStaffEditInfoV2({ hairdresserId: id });
  const payload = (res && res.data && (res.data.responseData || res.data.data)) || null;
  const u = payload && payload.userInfo;
  const h = u && u.hairdresser;
  if (!h) throw new SetupError('I couldn’t load your profile just now.');
  return { u, h };
}

// A placeholder avatar is not a photo.
const isRealPhoto = (url) => !!url && typeof url === 'string' && !/placeholder|default[-_]?(user|avatar|profile)/i.test(url);

export function profileOf({ u, h }) {
  const photo = u.userImage && u.userImage.circleImage;
  return {
    first: text(u.first_name) || firstWord(u.name),
    name: text(u.name) || [u.first_name, u.last_name].filter(Boolean).join(' '),
    email: u.email || '',
    photo: isRealPhoto(photo) ? photo : '',
    jobTitle: h.jobTitle || '',
    bio: h.shortBio || '',
    employmentTypeId: Number(h.employmentTypeId) || null,
  };
}

/** Job title or bio, on their own, through the partial profile update. */
export async function saveProfileText(patch) {
  const res = await hairdresserService.updateProfileV2(patch);
  const d = res && res.data;
  if (!d || d.errorCode || d.error || d.statusCode === 0) throw new SetupError(refusal(res, 'That didn’t save. Try again.'));
  return true;
}

const known = (v) => v !== undefined && v !== null && v !== '';

/**
 * The profile photo, through the same save EditStaff uses on your own
 * profile (api-team 9). editStaff isn't a partial update, so the record is
 * read again right before writing and staffEditForm sends back only what the
 * endpoint always writes, exactly as stored, plus the photo. Commission
 * rates other than the one the validator insists on, currency, bio, booking
 * switches and the phone number's format are never touched from here.
 */
export async function savePhoto(file, { id, jobTitle } = {}) {
  const img = await normaliseImage(file);
  const { u, h } = await loadEditInfo(id);
  let fd;
  try {
    // Their job title as stored; the one on screen only stands in when the
    // record has none, since the validator won't take the save without one.
    fd = staffEditForm(id, u, h, { jobTitle: text(h.jobTitle) ? undefined : text(jobTitle) || undefined, circleImage: img, rectangleImage: img }, { self: true });
  } catch (e) {
    throw new SetupError(e.message);
  }

  const res = await hairdresserService.editStaff(fd);
  const d = (res && res.data) || {};
  const refused = d.error || d.errorCode || d.statusCode === 0 || (d.statusCode !== undefined && d.statusCode !== 1);
  if (refused) throw new SetupError(refusal(res, 'Your photo didn’t save. Try again.'));

  // Only call it saved once the account shows a photo. A plain address can
  // stay the same when a photo is replaced, so a stamp makes it redraw; a
  // signed one is left exactly as it came.
  const after = profileOf(await loadEditInfo(id));
  if (!after.photo) throw new SetupError('Your photo didn’t come through. Try again, or use a different photo.');
  return after.photo.indexOf('?') === -1 ? `${after.photo}?v=${Date.now()}` : after.photo;
}

/* ---------------- hours: their own weekly rota ---------------- */

export const DAYS = [
  { key: 'mon', short: 'Mon', long: 'Monday' },
  { key: 'tue', short: 'Tue', long: 'Tuesday' },
  { key: 'wed', short: 'Wed', long: 'Wednesday' },
  { key: 'thu', short: 'Thu', long: 'Thursday' },
  { key: 'fri', short: 'Fri', long: 'Friday' },
  { key: 'sat', short: 'Sat', long: 'Saturday' },
  { key: 'sun', short: 'Sun', long: 'Sunday' },
];

export const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
export const fromHHMM = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
};
/** "9:00", "17:30": how times read in the setup. */
export const clock = (m) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

/**
 * Their weekly pattern from the team list row (api-team 10): week 1, and
 * whether the rota changes from week to week (multiWeek), in which case
 * setup shows it but never saves it.
 */
export function rotaOf(row) {
  const rota = (row && row.rotaInfo && row.rotaInfo.rota) || {};
  const week = firstWeekOf(rota.schedule) || {};
  const profiles = (week.profiles || [])
    .filter((p) => p && DAYS.some((d) => d.key === p.day) && Number.isFinite(Number(p.startTime)) && Number.isFinite(Number(p.endTime)))
    .map((p) => ({
      day: p.day,
      startTime: Number(p.startTime),
      endTime: Number(p.endTime),
      breakTimes: (p.breakTimes || []).filter((b) => b && known(b.startTime) && known(b.endTime)).map((b) => ({ startTime: Number(b.startTime), endTime: Number(b.endTime) })),
    }));
  return { profiles, startDate: rota.startDate || null, multiWeek: isMultiWeek(rota.schedule) };
}

/** The salon's opening hours as a starting week, when they have no rota yet. */
export async function loadOpeningWeek() {
  const salonId = localStorage.getItem('salonId');
  if (!salonId) return [];
  const res = await salonService.getOpeningHours(salonId);
  const d = (res && res.data) || {};
  const hours = (d.responseData && d.responseData.data && d.responseData.data.opening_hours) || (d.data && d.data.opening_hours) || [];
  if (!Array.isArray(hours)) return [];
  return DAYS.map((day) => {
    const h = hours.find((x) => x && x.dayName === day.long);
    if (!h || h.isClose) return null;
    const start = fromHHMM(h.sTime);
    const end = fromHHMM(h.eTime);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return null;
    return { day: day.key, startTime: start, endTime: end, breakTimes: [] };
  }).filter(Boolean);
}

/**
 * The whole week in one idempotent PUT, at the active location (api-team 11).
 * A day left out is a day off. The existing start date is kept, so pay and
 * past days aren't disturbed; a brand-new rota starts this Monday.
 */
export async function saveMyRota(id, profiles, startDate, multiWeek = false) {
  // A save replaces the whole rota with this one week, so a rota that
  // changes week to week is never written from here.
  if (multiWeek) throw new SetupError('Your rota changes from week to week, so change it in Team, under Schedules.');
  const existing = startDate && moment(startDate).isValid() ? moment(startDate).format('YYYY-MM-DD') : null;
  const order = DAYS.map((d) => d.key);
  const res = await hairdresserService.saveRota({
    hairdresserId: id,
    rotaStartDate: existing || moment().startOf('isoWeek').format('YYYY-MM-DD'),
    rota: [{
      weekNumber: 1,
      profiles: profiles
        .slice()
        .sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day))
        .map((p) => ({ day: p.day, startTime: p.startTime, endTime: p.endTime, breakTimes: p.breakTimes || [] })),
    }],
  });
  if (!res || !res.data || res.data.errorCode !== 0) {
    throw new SetupError(text(res && res.data && res.data.messages) || 'Your hours didn’t save. Try again.');
  }
  teamCache = null;
  return existing || moment().startOf('isoWeek').format('YYYY-MM-DD');
}

/* ---------------- services: their own rows ---------------- */

const catalogueOk = (res) => res && res.data && res.data.statusCode === 1;
const resultOf = (res) => (res && res.data && res.data.responseData && res.data.responseData.result) || null;

/**
 * Everything on the menu they can see, marked with whether they offer it and
 * at what price. The catalogue gives the services (api-menu 4); the diary's
 * own "what can this person be booked for" read gives their rows (api-menu 13).
 */
export async function loadMyServices(ids, meId) {
  const [list, grouped] = await Promise.all([
    newServices.listServices({ hairdresserId: '', onlineBooking: '', status: '', search: '' }),
    hairdresserService.getServicesGroupedV2({ hairdresserId: meId }),
  ]);
  if (!catalogueOk(list)) throw new SetupError(refusal(list, 'I couldn’t load the services just now.'));
  const offered = [];
  ((grouped && grouped.data && grouped.data.data && grouped.data.data.hairdresserSelectedServices) || []).forEach((g) => {
    (g.services || []).forEach((svc) => {
      if (!svc) return;
      offered.push({
        cid: svc.commonServiceId ? String(svc.commonServiceId) : null,
        name: svc.serviceName || '',
        category: (svc.category && svc.category.categoryName) || g.categoryName || 'Other',
        price: Number(svc.servicePrice) || 0,
        time: Number(svc.serviceTime) || 0,
      });
    });
  });

  const seen = new Set();
  const services = [];
  (resultOf(list) || []).forEach((cat) => {
    (cat.serviceList || []).forEach((s) => {
      const id = String(s.commonServiceId || s._id || '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      services.push({
        id,
        name: s.serviceName || 'Service',
        category: cat.categoryName || 'Other',
        categoryId: cat._id || null,
        salonTime: Number(s.serviceTime) || 0,
        salonPrice: Number(s.servicePrice) || 0,
      });
    });
  });

  const used = new Set();
  const merged = services.map((s) => {
    const m = offered.find((o, i) => !used.has(i) && o.cid === s.id) || offered.find((o, i) => !used.has(i) && !o.cid && norm(o.name) === norm(s.name));
    if (m) used.add(offered.indexOf(m));
    return m ? { ...s, on: true, price: m.price, time: m.time } : { ...s, on: false, price: null, time: null };
  });
  // Offered but not on the menu they can see: shown, not editable here.
  offered.forEach((o, i) => {
    if (used.has(i)) return;
    merged.push({ id: o.cid || `x-${i}`, name: o.name, category: o.category, on: true, price: o.price, time: o.time, readOnly: !o.cid, salonTime: o.time, salonPrice: o.price });
  });
  const shareService = list.data.responseData && list.data.responseData.shareService;
  return { services: merged, shareService, ids, meId };
}

/**
 * Only what they can be booked for, with their price and time: the diary's
 * own read (api-menu 13). Enough for the ready check, without the menu.
 */
export async function loadMyOffered(meId) {
  const res = await hairdresserService.getServicesGroupedV2({ hairdresserId: meId });
  const groups = res && res.data && res.data.data && res.data.data.hairdresserSelectedServices;
  if (!Array.isArray(groups)) throw new SetupError('I couldn’t check your services just now.');
  const out = [];
  groups.forEach((g) => (g.services || []).forEach((svc) => {
    if (svc) out.push({ name: svc.serviceName || '', price: Number(svc.servicePrice) || 0, time: Number(svc.serviceTime) || 0 });
  }));
  return out;
}

// One person's row, as ServiceEditorPanel saves it (api-menu 10).
function toRow(r, base, req) {
  return {
    hairdresserId: r.hairdresserId,
    servicePrice: parseFloat(r.servicePrice) || 0,
    serviceTime: parseFloat(r.serviceTime) || 0,
    // As the editor reads it: a row without the flag offers the service.
    isDeactivated: !!r.isDeactivated,
    depositPrice: parseFloat(r.depositPrice) || 0,
    onlineBooking: !!r.onlineBooking,
    processingTime: parseFloat(r.processingTime) || 0,
    finishingTime: parseFloat(r.finishingTime) || 0,
    setupTime: parseFloat(r.setupTime) || 0,
    cleanupTime: parseFloat(r.cleanupTime) || 0,
    requiresFinishing: !!r.requiresFinishing,
    finishingServiceIds: r.finishingServiceIds || [],
    finishingSameStylist: r.finishingSameStylist !== false,
    requiresConsultation: !!r.requiresConsultation,
    consultationServiceId: r.consultationServiceId || null,
    consultationSameStylist: r.consultationSameStylist !== false,
    finishingMessage: r.finishingMessage || '',
    consultationMessage: r.consultationMessage || '',
    diaryColour: r.diaryColour || base,
    isColourOverridden: !!(r.diaryColour && r.diaryColour !== base),
    linkedForms: (r.linkedForms || []).map((f) => ({ formId: f.formId, formName: f.formName, isRequired: f.isRequired !== false })),
    resourceRequirements: req,
  };
}

async function viewService(commonServiceId) {
  const res = await newServices.viewService({ commonServiceId });
  const v = catalogueOk(res) ? resultOf(res) : null;
  if (!v) throw new SetupError(refusal(res, 'I couldn’t open that service just now.'));
  return v;
}

/** Their own row on one service, with deposit, straight from the service. */
export async function loadMyRow(commonServiceId, ids) {
  const v = await viewService(commonServiceId);
  const r = (v.hairdresserService || []).find((x) => ids.includes(String(x.hairdresserId)));
  return r ? { on: !r.isDeactivated, price: parseFloat(r.servicePrice) || 0, time: parseFloat(r.serviceTime) || 0, deposit: parseFloat(r.depositPrice) || 0 } : null;
}

/**
 * Change only their own row on one service. The service is read fresh, and
 * every other row and field goes back exactly as it came, which is how the
 * service editor saves (api-menu 10). If they had no row yet, one is added and
 * checked afterwards, since the catalogue has never been asked to do that.
 */
export async function setMyServiceRow(commonServiceId, { ids, meId, patch, fill }) {
  const v = await viewService(commonServiceId);
  const base = v.diaryColour || '#c8eeee';
  const withReq = (v.hairdresserService || []).find((x) => Array.isArray(x.resourceRequirements) && x.resourceRequirements.length);
  const req = withReq
    ? withReq.resourceRequirements.filter((q) => q && q.groupId).map((q) => (q.resourceId ? { groupId: String(q.groupId), resourceId: String(q.resourceId) } : { groupId: String(q.groupId) }))
    : [];
  const rows = (v.hairdresserService || []).filter(Boolean).map((x) => toRow(x, base, req));
  let mine = rows.find((x) => ids.includes(String(x.hairdresserId)));
  const added = !mine;
  if (!mine) {
    mine = toRow({ hairdresserId: meId, isDeactivated: true }, base, req);
    rows.push(mine);
  }
  // `fill` only fills a blank: a price or time they set before is kept.
  Object.keys(fill || {}).forEach((k) => { if (!(Number(mine[k]) > 0)) mine[k] = fill[k]; });
  Object.assign(mine, patch);
  const cat = v.categoryID || v.categoryId || '';
  const categoryID = cat && typeof cat === 'object' ? String(cat._id || '') : String(cat || '');
  const res = await newServices.editServices({
    commonServiceId,
    serviceName: v.serviceName,
    categoryID,
    serviceDescription: v.serviceDescription || '',
    diaryColour: base,
    hairdresserService: rows,
  });
  if (!catalogueOk(res)) throw new SetupError(refusal(res, 'That didn’t save. Try again.'));
  if (added) {
    const check = await loadMyRow(commonServiceId, ids);
    if (!check) throw new SetupError('I couldn’t add you to this one from here. Ask the salon to switch you on for it.');
  }
  try { window.dispatchEvent(new CustomEvent('beu:catalogue-changed')); } catch (e) { /* nothing listening */ }
  return mine;
}

/**
 * A service of their own. Safe to repeat: an existing service with the same
 * name is switched on for them instead of being created again, and the
 * category is reused by name. Rows for the rest of the team go in switched
 * off, as the service editor creates them.
 */
export async function createMyService({ name, price, time, categoryName, ids, meId }) {
  const clean = String(name || '').trim();
  if (!clean) throw new SetupError('Give the service a name first.');
  const [cats, list, team] = await Promise.all([
    newServices.listCategory({}),
    newServices.listServices({}),
    newServices.listHairdresser({}).catch(() => null),
  ]);
  let existing = null;
  (resultOf(list) || []).forEach((c) => (c.serviceList || []).forEach((s) => {
    if (!existing && norm(s.serviceName) === norm(clean)) existing = String(s.commonServiceId || s._id);
  }));
  if (existing) {
    await setMyServiceRow(existing, { ids, meId, patch: { isDeactivated: false, onlineBooking: true, servicePrice: price, serviceTime: time } });
    return { id: existing, reused: true };
  }

  const wanted = String(categoryName || 'Services').trim() || 'Services';
  let categoryID = null;
  (resultOf(cats) || []).forEach((c) => { if (!categoryID && norm(c.categoryName) === norm(wanted)) categoryID = c._id; });
  if (!categoryID) {
    const made = await newServices.createCategory({ categoryName: wanted });
    if (!catalogueOk(made)) throw new SetupError(refusal(made, `I couldn’t add the ${wanted} category.`));
    const r = resultOf(made);
    categoryID = r && (r._id || (Array.isArray(r) && r[0] && r[0]._id));
    if (!categoryID) throw new SetupError(`I couldn’t add the ${wanted} category.`);
  }

  const base = '#c8eeee';
  const others = ((resultOf(team) || []).map((h) => h && h._id).filter(Boolean).map(String)).filter((x) => !ids.includes(x));
  const rows = [
    toRow({ hairdresserId: meId, isDeactivated: false, onlineBooking: true, servicePrice: price, serviceTime: time }, base, []),
    ...others.map((hid) => toRow({ hairdresserId: hid, isDeactivated: true }, base, [])),
  ];
  const res = await newServices.createServices({ serviceName: clean, categoryID, serviceDescription: '', diaryColour: base, hairdresserService: rows });
  if (!catalogueOk(res)) throw new SetupError(refusal(res, `I couldn’t add ${clean}.`));
  const out = resultOf(res);
  const id = Array.isArray(out) ? out[0] && out[0].commonServiceId : out && out.commonServiceId;
  try { window.dispatchEvent(new CustomEvent('beu:catalogue-changed')); } catch (e) { /* nothing listening */ }
  return { id: id ? String(id) : null, reused: false, categoryName: wanted };
}

/**
 * Take back a service they made themselves in setup (a typo, a duplicate).
 * Only ever called for a service this person created here, which nobody else
 * on the team is switched on for, so the salon's menu loses nothing it had.
 */
//
// The catalogue only deletes a service nobody is switched on for, and that
// includes them ("in use by other staff members", even when the only row is
// theirs: staging, oscarm, 6 Oct 2026). So their row goes off first, then the
// service goes. If the delete is still refused it stays switched off, which
// is the next best thing, and says so.
export async function deleteMyService(id, { ids, meId } = {}) {
  await setMyServiceRow(id, { ids, meId, patch: { isDeactivated: true } });
  const res = await newServices.deleteServices({ commonServiceId: id });
  if (!catalogueOk(res)) {
    try { window.dispatchEvent(new CustomEvent('beu:catalogue-changed')); } catch (e) { /* nothing listening */ }
    const err = new SetupError('I’ve switched it off for you, but it couldn’t be deleted.');
    err.switchedOff = true;
    throw err;
  }
  try { window.dispatchEvent(new CustomEvent('beu:catalogue-changed')); } catch (e) { /* nothing listening */ }
  return true;
}

export const TIME_OPTIONS = [10, 15, 20, 30, 40, 45, 60, 75, 90, 105, 120, 150, 180, 210, 240];
export const minutesLabel = (m) => {
  if (!m) return '';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

/* ---------------- privacy: their clients and their diary ---------------- */

const jsonHeaders = () => ({ 'Content-Type': 'application/json', accessToken: localStorage.getItem('accessToken') });

/**
 * Both client-sharing flags on their own row (api-money-rules 3). The
 * endpoint may refuse a self-employed token; Data sharing then falls back to
 * the signed-in profile, and so does this, saying where it came from.
 */
export async function loadClientSharing(ids, user) {
  try {
    const res = await axios.get(API.HAIRDRESSER_SHARING_SETTINGS, { headers: jsonHeaders() });
    const rows = res && res.data && res.data.responseData && res.data.responseData.data;
    const mine = Array.isArray(rows) ? rows.find((s) => s && ids.includes(String(s.hairdresserId))) : null;
    if (mine) return { clientShare: Number(mine.clientShare) === 1 ? 1 : 0, salonClientAccess: Number(mine.salonClientAccess) === 1 ? 1 : 0, cached: false };
  } catch (e) { /* the profile below */ }
  const h = (user && user.userInfo && (user.userInfo.hairdresser || user.userInfo.hairdresserInfo)) || {};
  if (h.clientShare === undefined && h.salonClientAccess === undefined) return null;
  return { clientShare: Number(h.clientShare) === 1 ? 1 : 0, salonClientAccess: Number(h.salonClientAccess) === 1 ? 1 : 0, cached: true };
}

/** Their own direction only: share my clients with the business (api-money-rules 6). */
export async function saveClientShare(on) {
  const res = await axios.put(API.HAIRDRESSER_CLIENT_SHARING, { clientShare: on ? 1 : 0 }, { headers: jsonHeaders() });
  if (!res || !res.data || !res.data.responseData || res.data.error) throw new SetupError(refusal(res, 'That didn’t save. Try again.'));
  return true;
}

/** Every diary row, and theirs (api-money-rules 8). */
export async function loadDiarySharing(ids) {
  const { staff, bulkShare } = await diarySharingService.fetchSettings();
  const mine = (staff || []).find((s) => s && ids.includes(String(s.hairdresserId))) || null;
  // bulkShare: the salon's "Share business diaries with the team" switch.
  return { staff: staff || [], mine, bulkShare: !!bulkShare };
}

/**
 * Their own diary row (api-money-rules 9). The PATCH reports any answer as
 * success, so it is read back before it counts as saved.
 */
export async function saveDiaryVisibility(rowId, mode, visibleTo) {
  await diarySharingService.updateStaffVisibility(rowId, { diaryVisibility: mode, diaryVisibleTo: visibleTo || [] });
  const { mine } = await loadDiarySharing([String(rowId)]);
  const got = mine && (mine.diaryVisibility || '');
  if (got !== mode) throw new SetupError('That didn’t save. Try again.');
  if (mode === 'selected') {
    const want = (visibleTo || []).map(String).sort().join(',');
    const have = ((mine && mine.diaryVisibleTo) || []).map(String).sort().join(',');
    if (want !== have) throw new SetupError('That didn’t save. Try again.');
  }
  return mine;
}

/** Who this person can see in the diary. Empty means everyone (api-money-rules 11). */
export async function loadMyVisibility() {
  const v = await diarySharingService.fetchMyVisibility();
  return { canSee: (v && v.canSee) || [], iAmOwner: !!(v && v.iAmOwner) };
}

/* ---------------- their link and their own booking switches ---------------- */

/** Their own booking switches and link (api-money-rules 21). */
export async function loadProProfile() {
  const res = await hairdresserService.getProfileV2();
  const d = res && res.data && res.data.responseData;
  if (!d) throw new SetupError(refusal(res, 'I couldn’t load your booking settings just now.'));
  return {
    online: d.onlineBookingEnabled !== false,
    marketplace: d.showOnMarketplace !== false,
    ownLink: d.useOwnBookingLink === true,
    onSalonPage: d.showOnSalonProfile !== false,
    slug: d.slug || null,
  };
}

/**
 * Part of the salon, or their own page: the same choice as Settings > Booking
 * link, and both flags written together so they can't drift apart (on
 * neither page would leave them unbookable). The server makes their own link
 * the first time they go independent, so the profile is read again after.
 */
export async function saveBookingMode(independent) {
  await saveProfileText({ useOwnBookingLink: independent, showOnSalonProfile: !independent });
  return loadProProfile();
}

/** Only the switch that changed, on their own profile (api-money-rules 22). */
export async function saveProFlags(patch) {
  return saveProfileText(patch);
}

/** Their link, the way Settings > Booking link builds it for a professional. */
export function bookingLinkFor(user, pro) {
  const web = (siteSetting.api && siteSetting.api.weburl) || '/';
  if (pro && pro.ownLink && pro.slug) return `${web}book/pro/${pro.slug}`;
  const h = (user && user.userInfo && user.userInfo.hairdresser) || {};
  const salon = h.salon || {};
  const salonSlug = salon.slug || salon._id || localStorage.getItem('salonId') || '';
  const person = h.userId || h._id || myId();
  return salonSlug && person ? `${web}book/${salonSlug}/professional/${person}` : '';
}

/** Whether the salon's booking page is on. A read only; null when it can't be told. */
export async function loadSalonBookingOn() {
  const salonId = localStorage.getItem('salonId');
  if (!salonId) return null;
  try {
    const res = await salonService.getSalonSettings(salonId);
    const rd = res && res.data && res.data.responseData;
    const data = (rd && rd.data) || rd;
    if (!data || data.onlineBookingEnabled === undefined) return null;
    return data.onlineBookingEnabled !== false;
  } catch (e) {
    return null;
  }
}

export async function copyText(value) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (e) { /* the fallback below */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const done = document.execCommand('copy');
    document.body.removeChild(ta);
    return !!done;
  } catch (e) {
    return false;
  }
}

/* ---------------- their cancellation policy ---------------- */

/**
 * Their own policy document (api-money-rules 16). A self-employed stylist has
 * their own: the salon's covers the owner and employed staff only.
 */
export async function loadPolicy() {
  const res = await settingService.getCancellationPolicy({ isActiveIntend: 0 });
  const d = res && res.data;
  const list = d && d.responseData && d.responseData.result;
  if (!d || (d.statusCode !== 1 && !Array.isArray(list))) throw new SetupError(refusal(res, 'I couldn’t load your cancellation policy just now.'));
  const p = (Array.isArray(list) && list[0]) || {};
  return {
    tiers: (p.cancellationPolicy || [])
      .filter((t) => t && Number(t.beforeBookingHour) > 0 && Number(t.amountInPercentage) > 0)
      .map((t) => ({ beforeBookingHour: Number(t.beforeBookingHour), amountInPercentage: Number(t.amountInPercentage) })),
    noShowStatus: Number(p.noShowStatus) === 1 ? 1 : 0,
    noShowFee: Number(p.noShowFeeInPercentage) || 100,
    feePayer: Number(p.noShowFeePaidBy) === 1 ? 1 : 2,
    rescheduleStatus: Number(p.rescheduleStatus) === 1 ? 1 : 0,
    rescheduleBeforeHours: known(p.rescheduleBeforeHours) ? Number(p.rescheduleBeforeHours) : 24,
    upToBefore: known(p.onlineBookingUptoBefore) ? Number(p.onlineBookingUptoBefore) : 0,
    upTo: known(p.onlineBookingUpto) ? Number(p.onlineBookingUpto) : 12,
  };
}

/**
 * The write replaces the whole document (api-money-rules 17), so it is built
 * from a fresh read every time, with only the changed part swapped in. If the
 * read fails nothing is written: a blind write could switch charges on.
 */
export async function savePolicy(change) {
  const cur = await loadPolicy();
  const next = { ...cur, ...change };
  const res = await settingService.updateCancellationPolicy({
    noShowPolicy: { noShowFeeInPercentage: Number(next.noShowFee) || 100, noShowStatus: next.noShowStatus ? 1 : 0 },
    onlineBookingUptoBefore: next.upToBefore,
    onlineBookingUpto: next.upTo,
    cancellationPolicies: (next.tiers || []).map((t) => ({ amountInPercentage: Number(t.amountInPercentage), beforeBookingHour: Number(t.beforeBookingHour) })),
    reschedulePolicy: { rescheduleStatus: next.rescheduleStatus ? 1 : 0, rescheduleBeforeHours: next.rescheduleBeforeHours },
  });
  if (!res || !res.data || Number(res.data.statusCode) !== 1) throw new SetupError(refusal(res, 'Your policy didn’t save. Try again.'));
  return next;
}

export const chargesOn = (p) => !!(p && (p.noShowStatus || (p.tiers || []).length));

/* ---------------- small hooks the chapters share ---------------- */

/** False once the component has gone, so late answers don't land. */
export function useAlive() {
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  return alive;
}

/** A "Saved" that shows for a moment after each save, per key. */
export function useFlash(ms = 2200) {
  const [on, setOn] = useState({});
  const timers = useRef({});
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  const flash = useCallback((key = 'all') => {
    setOn((cur) => ({ ...cur, [key]: true }));
    clearTimeout(timers.current[key]);
    timers.current[key] = setTimeout(() => setOn((cur) => ({ ...cur, [key]: false })), ms);
  }, [ms]);
  return [on, flash];
}

/**
 * One queue per key, so two quick changes to the same thing always land in
 * the order they were made, and the last one wins.
 */
export function useSerial() {
  const chains = useRef({});
  return useCallback((key, task) => {
    const prev = chains.current[key] || Promise.resolve();
    const next = prev.catch(() => null).then(task);
    chains.current[key] = next;
    return next;
  }, []);
}
